/**
 * ValidationRow - Individual validation table row component
 * 
 * Story 1.7: Updated with slide-out panel support
 * Story 1.8: Added action dropdown menu with View and Edit options
 * 
 * Displays a single validation result row with:
 * - Type (Menu Item, Modifier Group, Modifier)
 * - Name
 * - Validation indicators (✓, ✗, N/A) for each field
 * - Tooltips for missing languages
 * - Action dropdown menu with:
 *   - View: opens slide-out panel (menu items) or modal (modifiers/groups)
 *   - Edit: opens item in Grubcenter admin for editing
 * - Clickable row support (Story 1.6)
 */

/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import languageCodes from "../../utils/langCodes";
import { VALIDATION_TYPE_DISPLAY, VALIDATION_STATUS, VALIDATION_TYPES } from "../../utils/enums";

// URL path templates for Grubcenter admin edit pages
const MENU_ITEM_PATH_TEMPLATE = "/menu-management/menu-items";
const MODIFIER_GROUP_PATH_TEMPLATE = "/menu-management/modifier-groups";

const ValidationRow = ({ validationResult, menuId, baseLanguage, onOpenModal, onOpenPanel }) => {
  const location = useLocation();
  const { type, name, validation } = validationResult;

  /**
   * Get full language names from language codes
   */
  const getLanguageNames = (codes) => {
    if (!codes || codes.length === 0) return [];
    return codes.map(code => {
      // Handle case-insensitive lookup
      const lowerCode = code.toLowerCase();
      return languageCodes[lowerCode] || code;
    });
  };

  /**
   * Get the display text for validation type
   */
  const getTypeDisplay = () => {
    return VALIDATION_TYPE_DISPLAY[type] || type;
  };

  /**
   * Render validation indicator with optional tooltip
   */
  const renderValidationIndicator = (validationField, fieldType) => {
    if (!validationField) {
      return <span className="ls-validation-indicator ls-validation-indicator--na">N/A</span>;
    }

    const { status, missingLanguages } = validationField;

    if (status === VALIDATION_STATUS.NOT_APPLICABLE) {
      return <span className="ls-validation-indicator ls-validation-indicator--na">N/A</span>;
    }

    if (status === VALIDATION_STATUS.VALID) {
      return (
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--valid">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }

    // Missing or Invalid
    const hasMissingLanguages = missingLanguages && missingLanguages.length > 0;
    
    return (
      <div className="ls-validation-indicator-wrapper">
        <span className="ls-validation-indicator-circle ls-validation-indicator-circle--error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        {hasMissingLanguages && (
          <div className="ls-validation-tooltip">
            Missing languages: {getLanguageNames(missingLanguages).join(", ")}
          </div>
        )}
      </div>
    );
  };

  /**
   * Handle View button click and row click
   * Story 1.7: For menu items, opens slide-out panel
   * Story 1.3: For modifiers/groups, opens modal showing associated items
   */
  const handleViewClick = () => {
    // Story 1.7: Menu items open slide-out panel
    if (type === VALIDATION_TYPES.ITEM) {
      onOpenPanel(validationResult);
    } else if (
      type === VALIDATION_TYPES.MODIFIER_GROUP ||
      type === VALIDATION_TYPES.MODIFIER
    ) {
      // Story 1.3: Modifiers and modifier groups open modal
      onOpenModal(validationResult);
    }
  };

  /**
   * Get Grubcenter base URL based on isAdminGC query parameter
   * Story 1.8: Dynamic URL construction based on query parameter
   * 
   * Environment variables:
   * - REACT_APP_GC_URL: Non-admin Grubcenter URL
   * - REACT_APP_ADMIN_GC_URL: Admin Grubcenter URL
   * 
   * If isAdminGC=true: Returns REACT_APP_ADMIN_GC_URL
   * If isAdminGC=false or not set: Returns REACT_APP_GC_URL
   */
  const getGrubcenterBaseUrl = () => {
    // Parse query parameters
    const params = queryString.parse(location.search || '');
    const isAdminGC = params.isAdminGC === 'true';
    
    // Return the appropriate URL based on isAdminGC parameter
    if (isAdminGC) {
      return process.env.REACT_APP_ADMIN_GC_URL || null;
    } else {
      return process.env.REACT_APP_GC_URL || null;
    }
  };

  /**
   * ActionDropdown - Inline component for View/Edit actions
   * Story 1.8: Dropdown menu with View and Edit options
   */
  const ActionDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    /**
     * Get the CSS min-width value for the dropdown menu
     * Reads from computed styles to avoid hardcoding values that duplicate CSS
     * Memoized since CSS values don't change during component lifetime
     */
    const dropdownMenuMinWidth = useMemo(() => {
      // Create a temporary element with the dropdown menu class to read CSS value
      const tempElement = document.createElement('div');
      tempElement.className = 'ls-validation-action-dropdown-menu';
      tempElement.style.position = 'fixed';
      tempElement.style.visibility = 'hidden';
      tempElement.style.pointerEvents = 'none';
      document.body.appendChild(tempElement);
      
      try {
        const computedStyle = window.getComputedStyle(tempElement);
        const minWidth = Number.parseFloat(computedStyle.minWidth);
        return minWidth || 140; // Fallback to 140 if parsing fails
      } finally {
        tempElement.remove();
      }
    }, []); // Only calculate once on mount

    // Memoize Grubcenter base URL to avoid redundant parsing on every render
    // Only recalculate when location.search changes
    const gcUrl = useMemo(() => getGrubcenterBaseUrl(), [location.search]);
    const hasRecipeId = !!validationResult.recipeId;
    const canEdit = gcUrl && hasRecipeId;

    // Log warnings for missing configuration (only once per mount)
    useEffect(() => {
      if (!gcUrl) {
        const params = queryString.parse(location.search || '');
        const isAdminGC = params.isAdminGC === 'true';
        const missingVar = isAdminGC ? 'REACT_APP_ADMIN_GC_URL' : 'REACT_APP_GC_URL';
        console.warn(`${missingVar} environment variable is not defined. Edit action will be disabled.`);
      }
      if (!hasRecipeId && validationResult.id) {
        console.warn(`Validation item ${validationResult.id} is missing recipeId. Edit action will be disabled.`);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Click outside to close
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    // ESC key to close
    useEffect(() => {
      const handleEscKey = (event) => {
        if (event.key === 'Escape' && isOpen) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscKey);
      }

      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }, [isOpen]);

    // Close dropdown on scroll (since using fixed positioning)
    useEffect(() => {
      const handleScroll = () => {
        if (isOpen) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        window.addEventListener('scroll', handleScroll, true);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [isOpen]);

    // Close dropdown on window resize (since using fixed positioning)
    useEffect(() => {
      const handleResize = () => {
        if (isOpen) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        window.addEventListener('resize', handleResize);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [isOpen]);

    const toggleDropdown = (event) => {
      event.stopPropagation(); // Prevent row click
      
      if (!isOpen && dropdownRef.current) {
        // Calculate button position for fixed positioning
        const buttonRect = dropdownRef.current.getBoundingClientRect();
        
        // Use memoized CSS min-width value to avoid duplicating hardcoded values
        // This will be corrected with actual measured width in useEffect for pixel-perfect positioning
        const menuWidth = dropdownMenuMinWidth;
        
        // Set initial position (will be adjusted after measuring actual dropdown dimensions)
        // Position off-screen initially so we can measure it
        setMenuPosition({
          left: buttonRect.right - menuWidth,
          top: -9999
        });
      }
      
      setIsOpen(!isOpen);
    };

    // Measure dropdown dimensions and calculate position after it renders
    useEffect(() => {
      if (isOpen && menuRef.current && dropdownRef.current) {
        // Use requestAnimationFrame to ensure the menu has been painted before measuring
        requestAnimationFrame(() => {
          if (menuRef.current && dropdownRef.current) {
            // Measure the actual dropdown dimensions dynamically
            // This ensures accuracy even if menu items, padding, font sizes, or CSS min-width changes
            const menuRect = menuRef.current.getBoundingClientRect();
            const dropdownHeight = menuRect.height;
            const dropdownWidth = menuRect.width;
            
            // Get button position
            const buttonRect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - buttonRect.bottom;
            
            // Open upward only if close to viewport bottom (not enough space below)
            const shouldOpenUpward = spaceBelow < dropdownHeight;
            setOpenUpward(shouldOpenUpward);
            
            // Calculate menu position (fixed positioning)
            // Use measured width instead of hardcoded value to stay in sync with CSS
            const gap = 4; // Same gap for both directions
            const position = {
              left: buttonRect.right - dropdownWidth,
              top: shouldOpenUpward 
                ? buttonRect.top - dropdownHeight - gap 
                : buttonRect.bottom + gap
            };
            
            setMenuPosition(position);
          }
        });
      }
    }, [isOpen]);

    const handleDropdownViewClick = (event) => {
      event.stopPropagation();
      handleViewClick();
      setIsOpen(false);
    };

    const handleEditClick = (event) => {
      event.stopPropagation();
      
      if (!canEdit) return;

      const { recipeId } = validationResult;
      let editUrl;

      if (type === VALIDATION_TYPES.ITEM) {
        // Menu Item
        editUrl = `${gcUrl}${MENU_ITEM_PATH_TEMPLATE}/${recipeId}`;
      } else {
        // Modifier or Modifier Group
        editUrl = `${gcUrl}${MODIFIER_GROUP_PATH_TEMPLATE}/${recipeId}`;
      }

      window.open(editUrl, '_blank');
      setIsOpen(false);
    };

    return (
      <div className="ls-validation-action-dropdown" ref={dropdownRef}>
        <button
          className="ls-validation-action-dropdown-button"
          onClick={toggleDropdown}
          aria-label="Actions"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          type="button"
        >
          ⋮
        </button>

        {isOpen && (
          <div 
            ref={menuRef}
            className={`ls-validation-action-dropdown-menu ${openUpward ? 'ls-validation-action-dropdown-menu--upward' : ''}`}
            role="menu"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`
            }}
          >
            <button
              className="ls-validation-action-dropdown-item"
              onClick={handleDropdownViewClick}
              role="menuitem"
              type="button"
            >
              <span className="ls-action-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>View</span>
            </button>

            <button
              className="ls-validation-action-dropdown-item"
              onClick={handleEditClick}
              disabled={!canEdit}
              role="menuitem"
              type="button"
              title={!canEdit ? 'Edit unavailable - configuration required' : 'Edit item in Grubcenter'}
            >
              <span className="ls-action-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.333 2A1.886 1.886 0 0114 4.667l-9 9-3.667.666.667-3.666 9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>Edit</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <tr 
      className="ls-validation-table-row"
      onClick={handleViewClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleViewClick();
        }
      }}
    >
      <td className="ls-validation-table-cell">{getTypeDisplay()}</td>
      <td className="ls-validation-table-cell ls-validation-table-cell--name">
        {name}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--category">
        {type === VALIDATION_TYPES.ITEM ? (validationResult.categoryName || "-") : "-"}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {renderValidationIndicator(validation.image, "image")}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {renderValidationIndicator(validation.description, "description")}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {renderValidationIndicator(validation.nameTranslation, "nameTranslation")}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {renderValidationIndicator(validation.descriptionTranslation, "descriptionTranslation")}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {renderValidationIndicator(validation.price, "price")}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--indicator">
        {type !== VALIDATION_TYPES.ITEM
          ? renderValidationIndicator(null)
          : renderValidationIndicator(validation.nutritionInfo || { status: VALIDATION_STATUS.MISSING })}
      </td>
      <td className="ls-validation-table-cell ls-validation-table-cell--action">
        <ActionDropdown />
      </td>
    </tr>
  );
};

export default ValidationRow;

